import { supabase } from '../../shared/supabase/supabaseClient';
import type { ReportsQueryScope } from '../domain/reportsQueryScope';
import { reportsQueryScopeToRpcParams } from '../domain/reportsQueryScope';
import {
  mapPersonalPaymentMixFromRpc,
  mapPersonalTimeBandsFromRpc,
  mapPersonalTopProductsFromRpc,
} from '../domain/reportsPersonalMappers';
import type {
  PersonalBranchFacts,
  PersonalDashboardFacts,
  PersonalOperatorFacts,
} from '../domain/reportsPersonalTypes';
import type { SalesByCashierRow } from '../../sales/public-api';
import { pickIso } from './reportsRemoteShared';

function mapOperatorRow(r: Record<string, unknown>): PersonalOperatorFacts {
  const salesTotal = Number(r.sales_total ?? 0);
  const salesCount = Number(r.sales_count ?? 0);
  const margenReposicionTotal = Number(r.margen_reposicion_total ?? 0);
  const base: SalesByCashierRow = {
    cashierUserId: r.cashier_user_id != null ? String(r.cashier_user_id) : null,
    displayName: String(r.display_name ?? 'Sin usuario'),
    isOwner: Boolean(r.is_owner),
    salesCount,
    salesTotal,
    ticketAverage: Number(r.ticket_average ?? (salesCount > 0 ? salesTotal / salesCount : 0)),
    pendingPayments: Number(r.pending_payments ?? 0),
    fiscalSalesCount: Number(r.fiscal_sales_count ?? 0),
    nonFiscalSalesCount: Number(r.non_fiscal_sales_count ?? 0),
    margenReposicionTotal,
    sharePct: Number(r.share_pct ?? 0),
    firstSaleAt: pickIso(r, 'first_sale_at', 'firstSaleAt'),
    lastSaleAt: pickIso(r, 'last_sale_at', 'lastSaleAt'),
  };
  const ticketVsRaw = r.ticket_vs_30d_pct ?? r.ticketVs30dPct;
  return {
    ...base,
    daysWithSales: Number(r.days_with_sales ?? 0),
    periodDays: Number(r.period_days ?? 1),
    customLinePct: Number(r.custom_line_pct ?? 0),
    cashierMismatchCount: Number(r.cashier_mismatch_count ?? 0),
    cashSalesTotal: Number(r.cash_sales_total ?? 0),
    cashDifferenceTotal: Number(r.cash_difference_total ?? 0),
    cashClosedSessions: Number(r.cash_closed_sessions ?? 0),
    cashShortageSessions: Number(r.cash_shortage_sessions ?? 0),
    paymentMix: mapPersonalPaymentMixFromRpc(r.payment_mix ?? r.paymentMix),
    cashGapBandCount: Number(r.cash_gap_band_count ?? r.cashGapBandCount ?? 0),
    cashGapExampleDetail:
      r.cash_gap_example_detail != null
        ? String(r.cash_gap_example_detail)
        : r.cashGapExampleDetail != null
          ? String(r.cashGapExampleDetail)
          : null,
    marginPct: Number(r.margin_pct ?? r.marginPct ?? (salesTotal > 0 ? margenReposicionTotal / salesTotal : 0)),
    ticketAvg30d: Number(r.ticket_avg_30d ?? r.ticketAvg30d ?? 0),
    ticketVs30dPct: ticketVsRaw == null ? null : Number(ticketVsRaw),
    timeBands: mapPersonalTimeBandsFromRpc(r.time_bands ?? r.timeBands),
    topProducts: mapPersonalTopProductsFromRpc(r.top_products ?? r.topProducts),
    stockMovementCount: Number(r.stock_movement_count ?? r.stockMovementCount ?? 0),
    shrinkageTotal: Number(r.shrinkage_total ?? r.shrinkageTotal ?? 0),
    stockNoSalesDays: Number(r.stock_no_sales_days ?? r.stockNoSalesDays ?? 0),
    customerChargesTotal: Number(r.fiado_charges_total ?? r.customerChargesTotal ?? 0),
    customerPaymentsTotal: Number(r.fiado_payments_total ?? r.customerPaymentsTotal ?? 0),
    voidedCount: Number(r.voided_count ?? r.voidedCount ?? 0),
    voidedTotal: Number(r.voided_total ?? r.voidedTotal ?? 0),
    voidedAsCashierCount: Number(r.voided_as_cashier_count ?? r.voidedAsCashierCount ?? 0),
    voidedAsCashierTotal: Number(r.voided_as_cashier_total ?? r.voidedAsCashierTotal ?? 0),
    cashAvgDifference: Number(r.cash_avg_difference ?? r.cashAvgDifference ?? 0),
    cashShortagePct: Number(r.cash_shortage_pct ?? r.cashShortagePct ?? 0),
    cashAvgSessionHours: Number(r.cash_avg_session_hours ?? r.cashAvgSessionHours ?? 0),
  };
}

export async function fetchRemotePersonalFacts(
  branchId: string,
  fromIso: string,
  toIso: string,
  scope?: ReportsQueryScope | null
): Promise<PersonalDashboardFacts | null> {
  const { data, error } = await supabase.rpc('reports_branch_personal_facts', {
    p_branch_id: branchId,
    p_from: fromIso,
    p_to: toIso,
    ...reportsQueryScopeToRpcParams(scope),
  });
  if (error) {
    console.warn('[reportsRemote] reports_branch_personal_facts', error.message);
    return null;
  }
  const j = data as Record<string, unknown> | null;
  if (!j) return null;

  const branchRaw = (j.branch_facts ?? j.branchFacts) as Record<string, unknown> | undefined;
  const branchSalesTotal = Number(branchRaw?.sales_total ?? 0);
  const branchMarginTotal = Number(branchRaw?.branch_margin_total ?? branchRaw?.branchMarginTotal ?? 0);
  const branch: PersonalBranchFacts = {
    salesCount: Number(branchRaw?.sales_count ?? 0),
    salesTotal: branchSalesTotal,
    ticketAverage: Number(branchRaw?.ticket_average ?? 0),
    orphanSalesCount: Number(branchRaw?.orphan_sales_count ?? 0),
    orphanSalesTotal: Number(branchRaw?.orphan_sales_total ?? 0),
    customLinePct: Number(branchRaw?.custom_line_pct ?? 0),
    voidedSalesCount: Number(branchRaw?.voided_sales_count ?? 0),
    voidedSalesTotal: Number(branchRaw?.voided_sales_total ?? 0),
    branchMarginTotal,
    branchMarginPct: Number(
      branchRaw?.branch_margin_pct ??
        branchRaw?.branchMarginPct ??
        (branchSalesTotal > 0 ? branchMarginTotal / branchSalesTotal : 0)
    ),
  };

  const opsRaw = j.operators;
  const operators: PersonalOperatorFacts[] = Array.isArray(opsRaw)
    ? (opsRaw as Record<string, unknown>[]).map(mapOperatorRow)
    : [];

  return {
    periodDays: Number(j.period_days ?? 1),
    branch,
    operators,
  };
}
